import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { STRINGS } from './strings';
import { ICONS, IMAGES, NUM, NAV_HREFS, CONTACT_STATIC, GALLERY, HERO_IMAGES, RANGE_SPECS, MODELS } from './media';

const LanguageContext = createContext(null);

const zip = (icons, imgs, arr, extra = () => ({})) =>
  arr.map((item, i) => ({
    ...(icons ? { icon: icons[i] } : {}),
    ...(imgs ? { img: imgs[i] } : {}),
    ...item,
    ...extra(item, i),
  }));

// Merge language-independent media with translated strings into a single content object.
function compose(lang) {
  const t = STRINGS[lang] || STRINGS.fr;
  return {
    lang,
    nav: t.nav.map((label, i) => ({ label, href: NAV_HREFS[i] })),
    cta: t.cta,
    hero: t.hero,
    heroImage: HERO_IMAGES[lang] || HERO_IMAGES.fr,
    liveMap: t.liveMap,
    liveSection: t.liveSection,
    gallery: { ...t.gallery, images: GALLERY },
    sectors: { ...t.sectors, items: zip(ICONS.sectors, null, t.sectors.items) },
    problems: t.problems,
    connectivity: { ...t.connectivity, items: zip(ICONS.connectivity, null, t.connectivity.items) },
    solutions: {
      ...t.solutions,
      stats: NUM.stats.map((s, i) => ({ ...s, label: t.solutions.stats[i] })),
      items: t.solutions.items.map((it, i) => ({ ...it, img: IMAGES.solutions[i] })),
    },
    features: { ...t.features, items: zip(ICONS.features, null, t.features.items) },
    industries: {
      ...t.industries,
      items: t.industries.items.map((name, i) => ({ name, icon: ICONS.industries[i], img: IMAGES.industries[i] })),
    },
    product: {
      ...t.product,
      image: IMAGES.tag,
      specs: zip(ICONS.tagSpecs, null, t.product.specs),
      range: t.product.range.map((r, i) => ({ icon: ICONS.range[i], ...r, tech: RANGE_SPECS[i] })),
      models: MODELS,
      advantages: zip(ICONS.advantages, null, t.product.advantages),
    },
    useCases: { ...t.useCases, items: zip(ICONS.useCases, null, t.useCases.items) },
    caseStudies: t.caseStudies,
    roi: { ...t.roi, cfg: NUM.roi },
    savings: t.savings,
    guide: t.guide,
    testimonials: t.testimonials,
    pricing: {
      ...t.pricing,
      plans: t.pricing.plans.map((p, i) => ({ ...p, ...NUM.pricing[i] })),
    },
    faq: t.faq,
    contact: { ...t.contact, ...CONTACT_STATIC },
    footer: t.footer,
    liveAssetIcons: ICONS.liveAssets,
  };
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem('logitag_lang') || 'fr'; } catch (_) { return 'fr'; }
  });
  const setLang = useCallback((l) => {
    setLangState(l);
    try { localStorage.setItem('logitag_lang', l); } catch (_) {}
  }, []);
  const content = useMemo(() => compose(lang), [lang]);

  useEffect(() => {
    const seo = {
      fr: { title: "LOGITAG — Suivi d'actifs IoT & tags Bluetooth en Suisse", desc: "Localisez et gérez vos équipements, outils et stocks en temps réel grâce aux tags Bluetooth LOGITAG. Jusqu'à 8 ans d'autonomie, étanche IP67." },
      en: { title: "LOGITAG — IoT Asset Tracking & Bluetooth Tags in Switzerland", desc: "Locate and manage your equipment, tools and stock in real time with LOGITAG Bluetooth tags. Up to 8 years battery life, IP67 waterproof." },
      de: { title: "LOGITAG — IoT-Asset-Tracking & Bluetooth-Tags in der Schweiz", desc: "Orten und verwalten Sie Geräte, Werkzeuge und Bestände in Echtzeit mit LOGITAG Bluetooth-Tags. Bis zu 8 Jahre Laufzeit, IP67 wasserdicht." },
    };
    const s = seo[lang] || seo.fr;
    document.title = s.title;
    document.documentElement.lang = lang;
    const md = document.querySelector('meta[name="description"]');
    if (md) md.setAttribute('content', s.desc);
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, content }), [lang, setLang, content]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  return [ctx.lang, ctx.setLang];
}

export function useContent() {
  return useContext(LanguageContext).content;
}
